import {
  IItem,
  IItemData,
  IItemFieldError,
  IItemFieldErrorData,
  ITEM_NUMBER_MAX_LENGTH,
  ITEM_DESCRIPTION_MAX_LENGTH,
  ITEM_UNIT_MAX_LENGTH,
  ITEM_ABC_CODE_LENGTH,
  ITEM_FAMILY_CODE_MAX_LENGTH
} from './item.types';
import { ICountryData, Country } from '../../models/country';
import { IItemTag, ItemTag } from '../item-tag';

export class Item implements IItem {
  readonly item: string = '';
  readonly description: string = '';
  readonly unit: string = 'EA';
  readonly abc_code: string = '';
  readonly unit_cost: number = 0;
  readonly family_code: string = '';
  readonly last_gen_date?: Date | string;
  readonly is_phantom: boolean = false;
  readonly is_machine: boolean = false;
  readonly is_obsolete: boolean = false;
  readonly price: number = 0;
  readonly hs_code: string = '';
  readonly country_of_origin?: string | Country;

  readonly manually_created: boolean = false;
  readonly is_valid_price: boolean = true;

  readonly tags: IItemTag[] = [];

  constructor(data?: IItemData) {
    if (data) {
      this.item = data.item;
      this.description = data.description;

      if (data.unit) {
        this.unit = data.unit;
      }
      if (data.abc_code) {
        this.abc_code = data.abc_code;
      }

      this.unit_cost = data.unit_cost;

      if (data.family_code) {
        this.family_code = data.family_code;
      }
      if (data.last_gen_date) {
        this.last_gen_date = data.last_gen_date;
      }

      this.is_phantom = data.is_phantom;

      if (data.price) {
        const price = Number(data.price);

        if (!Number.isNaN(price)) {
          this.price = price;
        }
      }

      if (data.manually_created) {
        this.manually_created = data.manually_created;
      }

      this.is_machine = data.is_machine;
      this.is_valid_price = data.is_valid_price;

      if (data.hs_code) {
        this.hs_code = data.hs_code;
      }

      if (data.country_of_origin) {
        this.country_of_origin =
          typeof data.country_of_origin === 'string'
            ? data.country_of_origin
            : new Country(data.country_of_origin);
      }
      this.is_obsolete = data.is_obsolete;

      this.tags = data.tags?.map(tag => new ItemTag(tag)) ?? [];
    }
  }

  validate(): boolean {
    if (this.item.length > ITEM_NUMBER_MAX_LENGTH || this.item.length == 0) {
      return false;
    }
    if (
      this.description.length > ITEM_DESCRIPTION_MAX_LENGTH ||
      this.description.length == 0
    ) {
      return false;
    }
    if (this.unit.length > ITEM_UNIT_MAX_LENGTH) {
      return false;
    }
    if (this.abc_code.length > ITEM_ABC_CODE_LENGTH) {
      return false;
    }
    if (this.family_code.length > ITEM_FAMILY_CODE_MAX_LENGTH) {
      return false;
    }
    return true;
  }
}

export class ItemFieldError implements IItemFieldError {
  readonly item: string[] = [];
  readonly description: string[] = [];
  readonly unit: string[] = [];
  readonly abc_code: string[] = [];
  readonly unit_cost: string[] = [];
  readonly family_code: string[] = [];
  readonly last_gen_date: string[] = [];
  readonly is_phantom: string[] = [];
  readonly price: string[] = [];
  readonly hs_code: string[] = [];
  readonly is_machine: string[] = [];
  readonly country_of_origin: string[] = [];
  readonly is_obsolete: string[] = [];
  readonly tags: string[] = [];

  constructor(data?: IItemFieldErrorData) {
    if (data) {
      if (data.item) {
        this.item = data.item;
      }

      if (data.description) {
        this.description = data.description;
      }

      if (data.unit) {
        this.unit = data.unit;
      }

      if (data.abc_code) {
        this.abc_code = data.abc_code;
      }

      if (data.unit_cost) {
        this.unit_cost = data.unit_cost;
      }

      if (data.family_code) {
        this.family_code = data.family_code;
      }

      if (data.last_gen_date) {
        this.last_gen_date = data.last_gen_date;
      }

      if (data.is_phantom) {
        this.is_phantom = data.is_phantom;
      }

      if (data.price) {
        this.price = data.price;
      }

      if (data.hs_code) {
        this.hs_code = data.hs_code;
      }

      if (data.is_machine) {
        this.is_machine = data.is_machine;
      }

      if (data.country_of_origin) {
        this.country_of_origin = data.country_of_origin;
      }

      if (data.is_obsolete) {
        this.is_obsolete = data.is_obsolete;
      }

      if (data.tags) {
        this.tags = data.tags;
      }
    }
  }

  clearErrors(): void {
    this.item.splice(0, this.item.length);
    this.description.splice(0, this.description.length);
    this.unit.splice(0, this.unit.length);
    this.abc_code.splice(0, this.abc_code.length);
    this.unit_cost.splice(0, this.unit_cost.length);
    this.family_code.splice(0, this.family_code.length);
    this.last_gen_date.splice(0, this.last_gen_date.length);
    this.is_phantom.splice(0, this.is_phantom.length);
    this.is_machine.splice(0, this.is_machine.length);
    this.price.splice(0, this.price.length);
    this.hs_code.splice(0, this.hs_code.length);
    this.country_of_origin.splice(0, this.country_of_origin.length);
    this.is_obsolete.splice(0, this.is_obsolete.length);
    this.tags.splice(0, this.tags.length);
  }

  static isItemFieldError(data: IItemFieldErrorData): boolean {
    if (data.item && Array.isArray(data.item)) {
      return true;
    } else if (data.description && Array.isArray(data.description)) {
      return true;
    } else if (data.unit && Array.isArray(data.unit)) {
      return true;
    } else if (data.abc_code && Array.isArray(data.abc_code)) {
      return true;
    } else if (data.unit_cost && Array.isArray(data.unit_cost)) {
      return true;
    } else if (data.family_code && Array.isArray(data.family_code)) {
      return true;
    } else if (data.last_gen_date && Array.isArray(data.last_gen_date)) {
      return true;
    } else if (data.is_phantom && Array.isArray(data.is_phantom)) {
      return true;
    } else if (data.is_machine && Array.isArray(data.is_machine)) {
      return true;
    } else if (data.price && Array.isArray(data.price)) {
      return true;
    } else if (data.hs_code && Array.isArray(data.hs_code)) {
      return true;
    } else if (data.is_obsolete && Array.isArray(data.is_obsolete)) {
      return true;
    } else if (data.tags && Array.isArray(data.tags)) {
      return true;
    } else if (
      data.country_of_origin &&
      Array.isArray(data.country_of_origin)
    ) {
      return true;
    }

    return false;
  }
}
